import { formatUrl } from 'govbr.frontend.policy/helpers/url';
import isEmpty from 'lodash/isEmpty';
import { defineMessages } from 'react-intl';

const IMAGES_EXTENSIONS = ['jpeg', 'jpg', 'png', 'gif', 'webp', 'tiff', 'svg'];

const AUDIO_EXTENSIONS = ['mp3', 'ogg'];

const VIDEO_EXTENSIONS = ['mp4'];

const JAVASCRIPT_EXTENSIONS = ['js'];

const AUDIO_MIME_TYPES = [
  'audio/ogg',
  'audio/x-ogg',
  'audio/mp3',
  'audio/mpeg',
  'audio/x-mp3',
  'audio/x-mpeg',
  // firefox uploads ogg files with this mime type
  'video/ogg',
];

const JAVASCRIPT_MIME_TYPES = [
  'application/javascript',
  'text/javascript',
  'application/x-javascript',
];

// Tamanho máximo padrão para arquivos não-imagens (em MB)
const DEFAULT_FILE_MAX_SIZE_MB = 30;

// Extensões permitidas no sistema
const ALLOWED_FILE_EXTENSIONS = [
  'gif',
  'jpeg',
  'jpg',
  'png',
  'doc',
  'xls',
  'csv',
  'ppt',
  'docx',
  'xlsx',
  'pptx',
  'odt',
  'ods',
  'odp',
  'otp',
  'pdf',
  'txt',
  'zip',
  'webp',
];

const messages = defineMessages({
  missingAltText: {
    id: 'Fill the image alt text.',
    defaultMessage: 'Fill the image alt text.',
  },
  invalidAudioFileExtension: {
    id: 'Invalid audio file extension. Allowed types: {types}.',
    defaultMessage: 'Invalid audio file extension. Allowed types: {types}.',
  },
  invalidAudioFileMimeType: {
    id: 'Invalid audio file MIME type: {actual}. Allowed MIME types: {types}.',
    defaultMessage:
      'Invalid audio file MIME type: {actual}. Allowed MIME types: {types}.',
  },
  invalidJavaScriptFileExtension: {
    id: 'Invalid JavaScript file extension. Allowed types: {types}.',
    defaultMessage:
      'Invalid JavaScript file extension. Allowed types: {types}.',
  },
  missingTitleOrSubtitle: {
    id: 'Mandatory to fill in at least one of the Title or Subtitle fields.',
    defaultMessage:
      'Mandatory to fill in at least one of the Title or Subtitle fields.',
  },
});

const maxImageSize = () => {
  const request = new XMLHttpRequest();
  // O terceriro parametro 'false' faz com que a requisição seja síncrona.
  request.open('GET', formatUrl('/@image-size'), false);
  request.send(null);
  if (request.status === 200) {
    return JSON.parse(request.response)['max_image_size'];
  }
  return 0;
};

const isImageSizeValid = (size) => {
  const maxSize = maxImageSize();
  const fileSizeMb = size / 1024 / 1024;
  if (fileSizeMb <= maxSize) {
    return { valid: true };
  }
  return { valid: false, maxSize: maxSize };
};

const isImage = (type) => {
  const extension = type.split('/')[1];
  return IMAGES_EXTENSIONS.some(
    (image_extension) => image_extension === extension,
  );
};

/**
 * Valida se a extensão do arquivo é permitida
 * @param {File} file - Arquivo a ser validado
 * @param {string[]} allowedExtensions - Lista de extensões permitidas (opcional)
 * @returns {Object} - {valid: boolean, allowedExtensions: Array, fileExtension: string}
 */
const validateFileExtension = (
  file,
  allowedExtensions = ALLOWED_FILE_EXTENSIONS,
) => {
  if (!file || !file.name) {
    return {
      valid: false,
      allowedExtensions: allowedExtensions,
      fileExtension: '',
    };
  }

  const fileExtension = file.name.split('.').pop().toLowerCase();

  return {
    valid: allowedExtensions.includes(fileExtension),
    allowedExtensions: allowedExtensions,
    fileExtension: fileExtension,
  };
};

/**
 * Valida tamanho de arquivo baseado no tipo
 * @param {File} file - Arquivo a ser validado
 * @param {number} maxSizeMB - Tamanho máximo em MB (opcional)
 * @returns {Object} - {valid: boolean, maxSize: number, currentSizeMB: number}
 */
const validateFileSize = (file, maxSizeMB = null) => {
  if (!file) {
    return { valid: false, maxSize: 0, currentSizeMB: 0 };
  }

  const fileSizeMB = file.size / 1024 / 1024;
  let maxSize = maxSizeMB;

  // Se não foi especificado um tamanho máximo, usar configurações padrão
  if (!maxSize) {
    if (isImage(file.type)) {
      maxSize = maxImageSize(); // Usa a configuração do servidor para imagens
    } else {
      // Configuração padrão para todos os outros tipos de arquivo
      maxSize = DEFAULT_FILE_MAX_SIZE_MB;
    }
  }

  return {
    valid: fileSizeMB <= maxSize,
    maxSize: maxSize,
    currentSizeMB: Math.round(fileSizeMB * 100) / 100, // Arredondar para 2 casas decimais
  };
};

/**
 * Valida arquivo completo (extensão + tamanho)
 * @param {File} file - Arquivo a ser validado
 * @param {Object} options - Opções de validação
 * @returns {Object} - Resultado completo da validação
 */
const validateFileComplete = (file, options = {}) => {
  const { maxSizeMB = null, allowedExtensions = ALLOWED_FILE_EXTENSIONS } =
    options;

  const extensionValidation = validateFileExtension(file, allowedExtensions);
  const sizeValidation = validateFileSize(file, maxSizeMB);

  return {
    valid: extensionValidation.valid && sizeValidation.valid,
    extension: extensionValidation,
    size: sizeValidation,
    file: {
      name: file.name,
      type: file.type,
      size: file.size,
    },
  };
};

const missingLeadImageAltTextValidator = ({
  value,
  formData,
  formatMessage,
}) => {
  const leadImage = formData['image'];
  const trimmed = typeof value === 'string' ? value.trim() : value;
  const fieldsEmpty = (!trimmed || trimmed === '') && !isEmpty(leadImage);
  return fieldsEmpty ? formatMessage(messages.missingAltText) : null;
};

const blockImageAltTextValidator = ({ value, formData, formatMessage }) => {
  const imageField =
    formData['preview_image'] ||
    formData['href']?.[0]?.['image_field'] ||
    formData['href']?.[0]?.['hasPreviewImage'];
  const trimmed = typeof value === 'string' ? value.trim() : value;
  const fieldsEmpty = (!trimmed || trimmed === '') && imageField;
  return fieldsEmpty ? formatMessage(messages.missingAltText) : null;
};

const audioFileValidator = ({ value, formData, formatMessage }) => {
  const audioFile = value;
  if (!audioFile) {
    return null;
  }
  const fileExtension = audioFile.filename.split('.').pop().toLowerCase();

  const isValidExtension = AUDIO_EXTENSIONS.includes(fileExtension)
    ? null
    : formatMessage(messages.invalidAudioFileExtension, {
        types: AUDIO_EXTENSIONS.join(', '),
      });
  const isValidMimeType = AUDIO_MIME_TYPES.includes(audioFile['content-type'])
    ? null
    : formatMessage(messages.invalidAudioFileMimeType, {
        actual: audioFile['content-type'],
        types: AUDIO_MIME_TYPES.join(', '),
      });

  return isValidExtension || isValidMimeType;
};

const videoFileValidator = ({ value, formData, formatMessage }) => {
  const videoFile = value;
  if (!videoFile) {
    return null;
  }
  const fileExtension = videoFile.filename.split('.').pop().toLowerCase();

  const isValidExtension = VIDEO_EXTENSIONS.includes(fileExtension)
    ? null
    : formatMessage(messages.invalidVideoFileExtension, {
        types: VIDEO_EXTENSIONS.join(', '),
      });

  return isValidExtension;
};

const missingTitleOrSubtitle = ({ value, formData, formatMessage }) => {
  const fieldsEmpty =
    (!value || !value.trim()) &&
    (!formData['title'] || isEmpty(formData['title'].trim()));
  return fieldsEmpty ? formatMessage(messages.missingTitleOrSubtitle) : null;
};

const javaScriptFileValidator = ({ value, formData, formatMessage }) => {
  const jsFile = value;
  if (!jsFile) {
    return null;
  }
  const fileExtension = jsFile.filename.split('.').pop().toLowerCase();

  return JAVASCRIPT_EXTENSIONS.includes(fileExtension)
    ? null
    : formatMessage(messages.invalidJavaScriptFileExtension, {
        types: JAVASCRIPT_EXTENSIONS.join(', '),
      });
};

/**
 * Valida alt text com 6 regras de validação
 * @param {string} value - Alt text value
 * @returns {string} - Mensagem de erro ou string vazia se válido
 */
export const validateAltText = (value) => {
  if (!value || value.trim() === '') {
    return 'obrigatório';
  }

  const trimmed = value.trim();

  // Regra 1: Comprimento mínimo
  if (trimmed.length < 3) {
    return 'Mínimo 3 caracteres';
  }

  // Regra 2: Comprimento máximo
  if (trimmed.length > 100) {
    return 'Máximo 100 caracteres';
  }

  // Regra 3: Validar extensão de arquivo
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp'];
  const hasExtension = imageExtensions.some((ext) =>
    trimmed.toLowerCase().includes(ext),
  );
  if (hasExtension) {
    return 'Não inclua extensão de arquivo';
  }

  // Regra 4: Palavras genéricas isoladas
  const genericWords = [
    'imagem',
    'foto',
    'picture',
    'image',
    'screenshot',
    'img',
    'figura',
  ];
  const lowerValue = trimmed.toLowerCase();
  if (genericWords.includes(lowerValue)) {
    return 'Muito genérico, seja mais descritivo.';
  }

  // Regra 5: Padrões de ID de arquivo/câmera/sistema (ex.: IMG_12345, DSC_001, P1000001, PHOTO_456)
  if (/\b(IMG|IMAGE|DSC|PHOTO|P)\s*[_-]?\d+\b/i.test(trimmed)) {
    return 'Evite IDs de câmera ou sistema. Descreva o conteúdo.';
  }

  // Regra 6: Não deve conter URLs
  if (/https?:\/\/|www\./i.test(trimmed)) {
    return 'Não inclua URLs';
  }

  // Regra 7: Validação extra: não deve ser apenas números/caracteres especiais
  if (!/[a-záéíóúàâêôãõç\w]/i.test(trimmed)) {
    return 'Deve conter pelo menos algumas palavras';
  }

  return ''; // OK
};

/**
 * Valida legenda (mais permissiva)
 * @param {string} value - Legenda text
 * @returns {string} - Mensagem de erro ou string vazia se válido
 */
export const validateLegenda = (value) => {
  if (value && value.length > 500) {
    return 'Legenda pode ter no máximo 500 caracteres';
  }
  return '';
};

export {
  AUDIO_EXTENSIONS,
  AUDIO_MIME_TYPES,
  VIDEO_EXTENSIONS,
  JAVASCRIPT_EXTENSIONS,
  JAVASCRIPT_MIME_TYPES,
  isImageSizeValid,
  isImage,
  missingLeadImageAltTextValidator,
  blockImageAltTextValidator,
  validateFileComplete,
  audioFileValidator,
  missingTitleOrSubtitle,
  videoFileValidator,
  javaScriptFileValidator,
};
